import { TEST_IDS } from '@va/constants';
import { ChartContext, ChartTooltipMode } from '@va/types/charts';
import { Tooltip, TooltipContent, useTooltipContext } from '@va/ui/tooltips';
import { getParameterByName, removeUrlParameter, sleep } from '@va/util/helpers';
import { useWindowDimensions } from '@va/util/hooks';
import { ChartDataset } from 'chart.js';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { BasicChartTooltipComponent, BasicChartTooltipValues } from './BasicChartTooltipComponent';
import '../external-tooltip.scss';

type BasicExternalChartTooltipProps = {
  tooltipMode?: ChartTooltipMode;
  reportBlockId?: string;
  title?: string;
  subtitle?: string;
  valueTransformer?: (val: any) => any;
  zIndex?: number;
};

export const basicExternalChartTooltip = (context: ChartContext, props: BasicExternalChartTooltipProps) => {
  return <BasicExternalChartTooltip context={context} {...props} />;
};

type TooltipProps = {
  context: ChartContext;
} & BasicExternalChartTooltipProps;

export const BasicExternalChartTooltip = ({
  context,
  title,
  subtitle,
  valueTransformer,
  tooltipMode = 'fixed',
  reportBlockId,
  zIndex = 1700,
}: TooltipProps) => {
  const { isMobile } = useWindowDimensions();
  const [isTouchMoving, setIsTouchMoving] = useState(false);
  const [touchEnded, setTouchEnded] = useState(true);
  const [isTooltipInteractive, setTooltipInteractive] = useState(false);
  const { tooltip, chart } = context;

  const { height, top } = chart.chartArea;

  const history = useHistory();

  const isChartDataAvailable = useCallback(async () => {
    const maxRetry = 20;
    const delayMS = 100;
    let itr = 0;

    while (itr <= maxRetry) {
      if (!chart || !chart.data.datasets || chart.data.datasets.length === 0) {
        await sleep(delayMS);
        itr++;
      } else {
        return true;
      }
    }

    if (!chart || !chart.data.datasets || chart.data.datasets.length === 0) return false;
    return true;
  }, [chart]);

  const openHighlightedTooltip = useCallback(async () => {
    const highlighted_data_index_str = getParameterByName('highlighted_data_index');
    if (!highlighted_data_index_str) return;
    const targetedReportBlockId = getParameterByName('report_block');
    if (targetedReportBlockId === '') return;
    if (targetedReportBlockId !== reportBlockId) return;

    const updatedUrl = removeUrlParameter(['highlighted_data_index', 'report_block']);
    history.replace(updatedUrl);

    const highlighted_data_index = parseInt(highlighted_data_index_str);
    if (Number.isNaN(highlighted_data_index)) return;

    const ok = await isChartDataAvailable();
    if (!ok) {
      return;
    }

    const maxLength = chart.data.datasets[0].data.length;
    if (maxLength <= highlighted_data_index) return;

    const elements = chart.data.datasets.map((_, datasetIndex) => ({
      datasetIndex,
      index: highlighted_data_index,
    }));
    chart.tooltip?.setActiveElements(elements, {
      x: 0,
      y: 0,
    });
    if (chart.config.options) {
      chart.config.options.events = ['click'];
    }
    chart.update('none');
  }, [chart, history, isChartDataAvailable, reportBlockId]);

  useEffect(() => {
    openHighlightedTooltip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTouchMove = useCallback(() => {
    tooltip.opacity = 0;
    setIsTouchMoving(true);
  }, [tooltip]);

  const onTouchEnd = useCallback(() => {
    if (isTouchMoving) {
      tooltip.opacity = 0;
    }

    setTouchEnded(true);
    setIsTouchMoving(false);
  }, [isTouchMoving, tooltip]);

  const closeTooltip = useCallback(() => {
    tooltip.opacity = 0;
    setTouchEnded(false);
  }, [tooltip]);

  const handleOnScroll = useCallback(() => {
    if (tooltip.opacity === 0) return;
    if (chart.config.options) {
      chart.config.options.events = ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'];
    }
    chart.update('none');
    tooltip.opacity = 0;

    setTooltipInteractive(false);
  }, [chart, tooltip]);

  const debouncedOnScroll = useDebouncedCallback(() => {
    handleOnScroll();
  }, 250);

  const handleOnClick = useCallback(() => {
    if (tooltip.opacity === 0) return;
    setTooltipInteractive((prev) => {
      if (chart.config.options) {
        chart.config.options.events = prev ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'] : ['click'];
      }
      chart.update('none');
      return !prev;
    });
  }, [chart, tooltip]);

  useEffect(() => {
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchmove', onTouchMove);

    window.addEventListener('scroll', debouncedOnScroll, { capture: true });
    window.addEventListener('click', handleOnClick, { capture: true });

    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);

      window.removeEventListener('scroll', debouncedOnScroll);
      window.removeEventListener('click', handleOnClick);
    };
  }, [onTouchEnd, onTouchMove, handleOnClick, debouncedOnScroll]);

  const { offsetLeft } = chart.canvas;

  const placeholderSize = 2;
  const left = offsetLeft + tooltip.caretX - placeholderSize / 2;

  const { dataPoints } = tooltip;

  const currentValues = useMemo(() => {
    if (!dataPoints) return {};
    return dataPoints.reduce((acc, point) => {
      const { dataset, raw, dataIndex } = point;
      acc[dataset.label ?? ''] = {
        color: extractDatasetColor(dataset),
        value: raw as number,
        linkTo: dataset.linkToFunc?.({ dataIndex: dataIndex, datasetKey: dataset.id ?? '' }),
        // @ts-ignore
        onLinkClick: dataset.onLinkClick,
        // @ts-ignore
        linkLabel: dataset.linkLabel,
        // @ts-ignore
        labelExtras: dataset?.labelExtras,
      };
      return acc;
    }, {} as BasicChartTooltipValues);
  }, [dataPoints]);

  if (!dataPoints) return null;

  return (
    <div className='chart-tooltip-wrapper pointer-events-none'>
      <Tooltip
        placement={isMobile ? 'top' : undefined}
        interactive
        open={tooltip.opacity === 1 && !isTouchMoving && touchEnded}
      >
        {tooltipMode === 'fixed' && (
          <FixedTrigger
            opacity={tooltip.opacity}
            left={left}
            top={top}
            placeholderSize={placeholderSize}
            height={height}
          />
        )}
        {tooltipMode === 'follow-point' && (
          <PointFollowerTrigger opacity={tooltip.opacity} left={tooltip.caretX} top={tooltip.caretY} />
        )}
        <TooltipContent
          tooltipClassNames={classNames('rounded-xl! p-2 max-w-135!', {
            'pointer-events-auto': isTooltipInteractive,
            'pointer-events-none': !isTooltipInteractive,
          })}
          zIndex={zIndex}
        >
          <BasicChartTooltipComponent
            currentValues={currentValues}
            title={title}
            subtitle={subtitle || chart.tooltip?.title.at(0) || ''}
            closeTooltip={closeTooltip}
            valueTransformer={valueTransformer}
          />
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

type CommonTooltipTriggerProps = {
  opacity: number;
  top: number;
  left: number;
};

type TooltipTriggerProps = CommonTooltipTriggerProps & {
  height?: number;
  placeholderSize?: number;
  className?: string;
  bottom?: number;
};

const FixedTrigger = ({ left, top, opacity, height, placeholderSize }: TooltipTriggerProps) => {
  return (
    <TooltipTrigger
      left={left}
      opacity={opacity}
      top={top}
      className='chart-datapoint'
      bottom={0}
      height={height}
      placeholderSize={placeholderSize}
    />
  );
};
const PointFollowerTrigger = ({ left, top, opacity }: CommonTooltipTriggerProps) => {
  return <TooltipTrigger left={left} opacity={opacity} top={top} />;
};

const TooltipTrigger = ({ opacity, top, left, placeholderSize, height, className, bottom }: TooltipTriggerProps) => {
  const { refs, context } = useTooltipContext();

  useEffect(() => {
    context.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, left, height]);

  // Overlaps the datapoint in the graph
  return (
    <div
      data-testid={TEST_IDS.generic.chart.datapoint}
      ref={refs.setReference}
      className={classNames(
        'absolute',
        {
          invisible: opacity === 0,
        },
        className,
      )}
      style={{
        bottom: bottom,
        top: top,
        left: left,
        width: placeholderSize,
        maxHeight: height,
      }}
    />
  );
};

function extractDatasetColor(dataset: ChartDataset): string {
  if (dataset.baseColor) {
    return dataset.baseColor;
  }

  if (dataset.backgroundColor) {
    return dataset.backgroundColor as string;
  }

  return '#1E1E1E';
}
